import type { Booking, BookingType, FieldConfiguration, UserCharLookupOption } from '@/types';

export function userCharIndex(fieldName: string): number | undefined {
  const match = /^user char (\d+)$/i.exec(fieldName);
  const index = match ? Number(match[1]) : 0;
  return index >= 1 && index <= 20 ? index : undefined;
}

export function activeUserCharOptions(type: Pick<BookingType, 'userCharOptions'> | undefined | null, index: number) {
  return (type?.userCharOptions ?? []).filter(o => o.fieldIndex === index && !o.isDeleted)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function matchesUserCharOption(option: UserCharLookupOption, value: string, optionId?: number) {
  return (!optionId || option.id === optionId)
    && option.value.trim().toLowerCase() === value.trim().toLowerCase();
}

export function lookupListError(options: UserCharLookupOption[]): string | undefined {
  const active = options.filter(o => !o.isDeleted);
  if (active.length === 0) return 'Add at least one lookup value.';
  if (active.some(o => !o.value.trim())) return 'Lookup values cannot be blank.';
  if (active.some(o => o.value.trim().length > 255)) return 'Lookup values cannot exceed 255 characters.';
  if (new Set(active.map(o => o.value.trim().toLowerCase())).size !== active.length)
    return 'Lookup values must be unique (ignoring case and surrounding spaces).';
  if (active.filter(o => o.isDefault).length > 1) return 'Choose no more than one default.';
}

export function missingUserCharLists(type: Pick<BookingType, 'fieldConfigurations' | 'userCharOptions'>): FieldConfiguration[] {
  return type.fieldConfigurations.filter(f => {
    const index = userCharIndex(f.fieldName);
    return index && (f.isVisible || f.showOnCustomTab)
      && !!lookupListError(activeUserCharOptions(type, index));
  });
}

export function initializeUserChar(type: BookingType, values: Record<number, string> = {}, ids: Record<number, number> = {}) {
  const nextValues = { ...values };
  const nextIds = { ...ids };
  for (const field of type.fieldConfigurations) {
    const index = userCharIndex(field.fieldName);
    if (!index || (!field.isVisible && !field.showOnCustomTab)) continue;
    if (nextValues[index]) continue;
    const defaultOption = activeUserCharOptions(type, index).find(o => o.isDefault);
    if (!defaultOption) continue;
    nextValues[index] = defaultOption.value;
    if (defaultOption.id) nextIds[index] = defaultOption.id;
  }
  return { values: nextValues, ids: nextIds };
}

export function validateUserCharBooking(type: BookingType | undefined | null,
  booking: Pick<Booking, 'userChar' | 'userCharOptionIds' | 'bookingTypeId'>, previous?: Booking): string | undefined {
  if (!type) return;
  const sameType = previous && String(previous.bookingTypeId) === String(type.id);
  const missing = missingUserCharLists(type);
  if (!sameType && missing.length)
    return `Booking type "${type.name}" requires administrator configuration for: ${missing.map(f => f.customName || f.fieldName).join(', ')}.`;
  for (const field of type.fieldConfigurations) {
    const index = userCharIndex(field.fieldName);
    if (!index || (!field.isVisible && !field.showOnCustomTab)) continue;
    const value = booking.userChar?.[index] ?? '';
    const id = booking.userCharOptionIds?.[index];
    if (sameType && value && value === previous.userChar?.[index]
      && (!id || id === previous.userCharOptionIds?.[index])) continue;
    if (!value) {
      if (field.isCompulsory || field.isRequired) return `Select a value for ${field.customName || field.fieldName}.`;
      continue;
    }
    const options = activeUserCharOptions(type, index);
    if (!options.some(o => matchesUserCharOption(o, value, id)))
      return `Select an active lookup value for ${field.customName || field.fieldName}.`;
  }
}
